import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { Typography, useMediaQuery } from "@mui/material";
import type { SxProps, TypographyProps } from "@mui/material";
import statutMap, { unknownStatus } from "constants/SatutMap";
import type { StatutAssociation } from "constants/SatutMap";
import type Statut from "constants/Statut";
import { useDebounce } from "use-debounce";
import ApplicationRoles from "constants/ApplicationRoles";
import { useUser } from "@sdeapps/react-core";

interface StatutChipProps extends TypographyProps {
  statut: Statut;
  isSmall?: boolean;
  sx?: SxProps;
}

function StatutChip({
  statut,
  variant = "body2",
  isSmall = false,
  sx,
  ...typographyProps
}: Readonly<StatutChipProps>): ReactNode {
  const [chipAssociation, setChipAssociation] = useState<StatutAssociation>(unknownStatus);

  const user = useUser();

  const sizeIsSmall = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const isSmallScreen = isSmall || sizeIsSmall;
  // on debounce isSmallScreen pour une question de performance, trop de rerenders inutiles sinon
  const [debouncedIsSmallScreen] = useDebounce(isSmallScreen, 500);

  useEffect(() => {
    setChipAssociation(statutMap.get(statut) ?? unknownStatus);
  }, [statut]);

  return (
    <Typography
      variant={variant}
      color={`${chipAssociation.color}.main`}
      {...typographyProps}
      sx={{
        backgroundColor: `${chipAssociation.color}.light`,
        paddingX: 1,
        paddingY: 0.5,
        fontWeight: 600,
        borderRadius: 1,
        display: "inline-block",
        ...sx,
      }}>
      {(user.isRoles(ApplicationRoles.SSTMANAGER) || user.isRoles(ApplicationRoles.STOREMANAGER)) &&
      debouncedIsSmallScreen
        ? chipAssociation.smallLabel
        : chipAssociation.label}
    </Typography>
  );
}

export default StatutChip;
